import { unescapeHTML } from "./library";
import { parseTypewriter, typeWriterHandler } from "./typewriter";

export async function executeCode(element) {
  const { target, indices } = parseTypewriter(element);
  if (target == null) {
    return;
  }

  // we want to stop typing
  typeWriterHandler.stopTheCurrentTypewriter();

  const css = getSourceCode("tricss", target, indices);
  const html = unescapeHTML(getSourceCode("trihtml", target, indices));
  const code = getSourceCode("tricode", target, indices);

  const codeElement = target.querySelector("code") as HTMLDivElement;
  if (codeElement == null) {
    return;
  }

  const language = Array.from(codeElement.classList)
    .find((c) => c.startsWith("language-"))
    ?.split("-")[1];

  const libraries = codeElement.getAttribute("data-libraries");
  const style = css + "\n" + (codeElement.getAttribute("data-style") || "");

  const worldId = target.parentElement.classList.contains("tricodewindow")
    ? target.parentElement.getAttribute("data-world")
    : "";

  runCode(
    code,
    "console",
    {
      libraries,
      style,
      html,
      worldId,
    },
    {
      language,
    }
  );
}

export function interactiveCode() {
  const code = document.querySelectorAll("pre");
  code.forEach((element) => {
    executeSingleCode(element);
  });

  // iframes

  const iframes = document.querySelectorAll("iframe[data-code-source]");
  iframes.forEach((element) => {
    const codeSource: string = element.getAttribute("data-code-source");

    element.__runner = () =>
      runCode(
        "",
        codeSource,
        {
          iframe: element,
          replaceFrom: -1,
          replaceTill: -1,
        },
        {
          language: "typescript",
        }
      );
  });
}

export function runInteractiveCode() {
  const code = Reveal.getCurrentSlide().querySelector("pre.allow-execute");

  if (code) {
    code.__runner();
  }

  const iframes = Reveal.getCurrentSlide().querySelectorAll(
    "iframe[data-code-source]"
  );
  iframes.forEach((element) => {
    element.__runner();
  });
}

async function runCode(
  code: string,
  codeSource: string,
  options: {},
  { language }: { language: string }
) {
  const slide = Reveal.getCurrentSlide();
  const runner = window.parent.__codeRunner;
  if (!runner) {
    console.error(
      "❌ Code execution not enabled, could not find a good runner for the code"
    );
    return;
  }

  // .filter(
  //   (f) => f.getAttribute("data-code-source") != null
  // );
  const iframe = slide.querySelector("iframe");
  if (!iframe) {
    console.error(
      "❌ Code execution not enabled, please attach an iframe that is capable of running the code."
    );
  }

  const typescriptRunner = runner[language];

  if (typescriptRunner == null) {
    console.error(
      `❌  Code execution not enabled, could not find a runner for ${language}. Available runners are ${Object.keys(
        runner
      ).join(", ")}`
    );
    return;
  }

  // const replaceFrom = iframe.getAttribute("data-code-replace-from");
  // const replaceTill = iframe.getAttribute("data-code-replace-till");

  const result = await typescriptRunner.renderResult(
    { window, document },
    unescapeHTML(code),
    codeSource,
    options
  );
  iframe.classList.add("iframeExecuted");
  iframe.srcdoc = result;
}

function executeSingleCode(element) {
  const codeElements = Array.from(
    element.querySelectorAll("code")
  ) as HTMLDivElement[];
  if (codeElements.length == 0) {
    return;
  }

  for (const codeElement of codeElements) {
    const codeText = codeElement.dataset.source || codeElement.innerHTML;
    // remember the source code
    codeElement.dataset.source = codeText;

    // replace the original code to make sure wa are always handling the sunhighlighted source
    if (codeElement.innerHTML.trim() !== codeText) {
      codeElement.innerHTML = codeText;
      delete codeElement.dataset.highlighted;
    }
  }

  const codeElement = codeElements[0];

  const codeText = codeElement.dataset.source || codeElement.innerHTML;
  const language = Array.from(codeElement.classList)
    .find((c) => c.startsWith("language-"))
    ?.split("-")[1];
  const replaceFrom = codeElement.getAttribute("data-code-replace-from");
  const replaceTill = codeElement.getAttribute("data-code-replace-till");
  const codeSource = codeElement.getAttribute("data-code-source");
  const libraries = codeElement.getAttribute("data-libraries");
  const style = codeElement.getAttribute("data-style");
  const worldId = element.parentElement.classList.contains("tricodewindow")
    ? element.parentElement.getAttribute("data-world")
    : "";

  element.__runner = () =>
    runCode(
      codeText,
      codeSource,
      {
        replaceFrom,
        replaceTill,
        libraries,
        style,
        worldId,
      },
      {
        language,
      }
    );

  if (codeElement.classList.contains("allow-execute")) {
    element.addEventListener("click", (e) => {
      if (e.offsetX < element.offsetWidth - 85 || e.offsetY > 35) {
        return;
      } else {
        element.__runner();
      }
    });
  }
}

function getSourceCode(sourceId, target, indices) {
  const codeElement = target.querySelector("code");
  if (codeElement == null) {
    return;
  }

  // find root section
  let section = target;
  while (section.tagName != "SECTION") {
    section = section.parentElement;
  }

  if (target.id != sourceId) {
    const codeElement = section.querySelector(`#${sourceId} code`);
    if (codeElement == null) {
      return;
    }
    return codeElement.dataset.source;
  }

  let codeText = codeElement.dataset.source || codeElement.innerHTML.trim();
  if (indices.length) {
    codeText = codeText
      .split("\n")
      .filter((_, i) => indices.includes(i + 1))
      .join("\n");
  }

  return codeText;
}

export function addTricodeRunners() {
  const runners = document.querySelectorAll(".tricodewindow");
  runners.forEach((element) => {
    const toolbar = element.querySelector('div[data-role="toolbar"]');
    // add a new button
    let runButton: HTMLButtonElement = toolbar.querySelector(".editor");
    if (runButton != null) {
      runButton.remove();
    }
    runButton = document.createElement("button");
    runButton.innerText = "Editor";
    runButton.className = "editor";

    runButton.onclick = async () => {
      let tricodeEditor: HTMLDivElement =
        document.querySelector(".tricodeeditor");

      if (tricodeEditor != null) {
        tricodeEditor.remove();
        tricodeEditor = null;
      }

      if (tricodeEditor == null) {
        tricodeEditor = document.createElement("div");

        tricodeEditor.className = "tricodeeditor";
        tricodeEditor.innerHTML = `
<iframe id="tricodeedditorframe" src="/embed/dynamic/code"></iframe>
          `;
        document.body.appendChild(tricodeEditor);
      }

      // make it visible
      tricodeEditor.style.display = "block";

      // find the iframe and get the window from it
      const iframe = tricodeEditor.querySelector("iframe");
      const win = iframe.contentWindow;

      let loader = (win as any).__exerciseLoader;
      while (loader == null) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        loader = (win as any).__exerciseLoader;
        console.log("waiting for loader");
      }

      const files = [];
      const tricode = element.querySelector("#tricode");
      if (tricode != null) {
        const code = tricode.querySelector("code")?.getAttribute("data-source");
        if (code) {
          files.push({
            id: "index.tsx",
            type: "userCode",
            name: "index.tsx",
            source: unescapeHTML(code),
          });
        }
      }

      const tricss = element.querySelector("#tricss");
      if (tricss != null) {
        const code = tricss.querySelector("code")?.getAttribute("data-source");
        if (code) {
          files.push({
            id: "index.css",
            type: "userCode",
            name: "index.css",
            source: unescapeHTML(code),
          });
        }
      }

      const trihtml = element.querySelector("#trihtml");
      if (trihtml != null) {
        const code = trihtml.querySelector("code")?.getAttribute("data-source");
        if (code) {
          files.push({
            id: "index.html",
            type: "userCode",
            name: "index.html",
            source: unescapeHTML(code),
          });
        }
      }

      loader.close = () => {
        const editor: HTMLDivElement = document.querySelector(".tricodeeditor");
        if (editor) {
          editor.style.display = "none";
        }
      };
      loader.config = {
        display: "editor",
        console: "always",
        exercise: {
          worldId: "ZNyKM",
          files,
        },
      };
    };

    toolbar.appendChild(runButton);
  });
}
